import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiErr } from "@/lib/api";
import { fdt } from "@/lib/format";
import { PageHeader } from "@/components/pawkt/PageHeader";
import { Loading, Empty } from "@/components/pawkt/Empty";
import { Button } from "@/components/ui/button";
import { MessageSquare, Mail, Phone, CreditCard, RefreshCw, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { WhatsAppConnectCard } from "@/components/pawkt/WhatsAppConnectCard";
import { WhatsAppInbox } from "@/components/pawkt/WhatsAppInbox";

const LABELS = { accepted: "Accepted", delivered: "Delivered", read: "Read", sent: "Sent (legacy, unverified)", pending: "Pending", unknown: "Unknown outcome",
  failed: "Failed", skipped: "Skipped", logged: "Logged (demo, not sent)", sending: "Sending", queued: "Queued (not sent)" };

const StatusPill = ({ status }) => {
  const map = {
    accepted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    delivered: "bg-emerald-100 text-emerald-800 border-emerald-300",
    read: "bg-emerald-100 text-emerald-800 border-emerald-300",
    sent: "bg-slate-100 text-slate-600 border-slate-200",
    pending: "bg-amber-50 text-amber-700 border-amber-200",
    unknown: "bg-orange-50 text-orange-700 border-orange-200",
    failed: "bg-rose-50 text-rose-700 border-rose-200",
    skipped: "bg-slate-100 text-slate-600 border-slate-200",
    logged: "bg-slate-100 text-slate-500 border-slate-200",
  };
  return <span data-testid={`comm-status-${status}`} title={LABELS[status]} className={`rounded-full border px-2 py-0.5 text-xs font-medium ${map[status] || "bg-slate-100 text-slate-600"}`}>{LABELS[status] || status}</span>;
};

const STATE = { not_configured: ["Not configured", "text-slate-400", XCircle], disabled: ["Demo — live sending off (logged, not sent)", "text-amber-600", XCircle],
  ready_for_testing: ["Ready for testing — live sending off", "text-sky-600", CheckCircle2], connected: ["Live", "text-emerald-600", CheckCircle2], error: ["Error", "text-rose-600", XCircle], demo: ["Demo mode — logged, not sent", "text-amber-600", XCircle] };

const IntegrationCard = ({ icon: Icon, label, connected, mode, state, detail, onVerify, verifying }) => {
  const [txt, cls, SI] = STATE[state || (connected ? "connected" : mode === "demo" ? "demo" : "not_configured")];
  const slug = label.toLowerCase().replace(/\s/g, "-");
  return (
    <div data-testid={`integration-${slug}`} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className="grid h-10 w-10 place-items-center rounded-lg bg-slate-100"><Icon className="h-5 w-5 text-slate-700" /></div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-slate-900">{label}</p>
        <p className={`flex items-center gap-1 text-xs font-medium ${cls}`} data-testid={`integration-${slug}-state`}><SI className="h-3.5 w-3.5" />{txt}</p>
        {detail && <p className="truncate text-[11px] text-slate-400" title={detail}>{detail}</p>}
      </div>
      {onVerify && <button onClick={onVerify} disabled={verifying} data-testid={`integration-${slug}-verify`} className="rounded-full border border-slate-200 px-3 py-1 text-xs text-slate-600 hover:bg-slate-50 disabled:opacity-50">{verifying ? "Checking…" : "Verify"}</button>}
    </div>
  );
};

export default function Communications() {
  const qc = useQueryClient();
  const { data: csd } = useQuery({ queryKey: ["comm-settings"], queryFn: async () => (await api.get("/settings/communication")).data });
  const cs = csd?.channels;
  const cv = csd?.can_verify;
  const verify = useMutation({
    mutationFn: (ch) => api.post(`/settings/communication/verify/${ch}`),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["comm-settings"] }); toast.message(`${r.data.channel}: ${r.data.last_check?.detail || r.data.state}`); },
    onError: (e) => toast.error(apiErr(e)),
  });
  const [filters, setFilters] = useState({ channel: "", status: "", event: "" });
  const qs = new URLSearchParams(Object.fromEntries(Object.entries(filters).filter(([, v]) => v))).toString();

  const { data: integ } = useQuery({ queryKey: ["integrations"], queryFn: async () => (await api.get("/settings/integrations")).data });
  const { data: stats } = useQuery({ queryKey: ["comm-stats"], queryFn: async () => (await api.get("/communications/stats")).data });
  const { data, isLoading } = useQuery({ queryKey: ["comms", qs], queryFn: async () => (await api.get(`/communications?${qs}`)).data });

  const run = useMutation({
    mutationFn: () => api.post("/communications/run-scheduler"),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["comms"] }); qc.invalidateQueries({ queryKey: ["comm-stats"] }); toast.success("Your business queue was processed"); },
    onError: (e) => toast.error(apiErr(e)),
  });

  const recover = useMutation({
    mutationFn: (id) => api.post(`/comm/outbox/${id}/recover`),
    onSuccess: (r) => { qc.invalidateQueries({ queryKey: ["comms"] }); toast.message(r.data.recovered ? "Marked interrupted — outcome unknown, review before sending again" : `Status: ${r.data.status}`); },
    onError: (e) => toast.error(e?.response?.data?.detail?.msg || apiErr(e)),
  });

  const pay = integ?.payment || {};
  const comm = integ?.communication || {};
  const c = stats?.counts || {};
  const qh = stats?.queue_health;

  return (
    <div>
      <PageHeader title="Communications" subtitle="Delivery status, reminders & channel health">
        <Button onClick={() => run.mutate()} disabled={run.isPending} data-testid="run-scheduler-btn" className="bg-slate-900 text-white gap-1.5">
          <RefreshCw className={`h-4 w-4 ${run.isPending ? "animate-spin" : ""}`} /> Process queue
        </Button>
      </PageHeader>

      {(comm?.whatsapp?.mode !== "live" || comm?.email?.mode !== "live" || comm?.sms?.mode !== "live") && (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3 text-sm text-amber-700" data-testid="comm-demo-banner">
          <MessageSquare className="h-4 w-4" /> Some channels are in demo mode — those messages are recorded here but <b className="mx-1">not delivered to customers</b> until a provider is configured.
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <IntegrationCard icon={CreditCard} label="Payment Gateway" connected={pay?.razorpay?.connected} mode={pay?.razorpay?.connected ? "live" : "demo"} />
        <IntegrationCard icon={Mail} label="Email" state={cs?.email?.state} detail={cs?.email?.last_check?.detail} onVerify={cv ? () => verify.mutate("email") : null} verifying={verify.isPending} />
        <IntegrationCard icon={MessageSquare} label="WhatsApp" state={cs?.whatsapp?.state} detail={cs?.whatsapp?.missing?.length ? `Missing: ${cs.whatsapp.missing.join(", ")}` : cs?.whatsapp?.last_check?.detail} onVerify={cv ? () => verify.mutate("whatsapp") : null} verifying={verify.isPending} />
        <IntegrationCard icon={Phone} label="SMS" connected={comm?.sms?.connected} mode={comm?.sms?.mode} />
      </div>

      <p className="mt-2 text-xs text-slate-500" data-testid="comm-platform-note">Email is platform-managed. WhatsApp is sent only from your business's own connected WhatsApp Business account. No credentials are ever entered or shown here. Invoice sends are blocked until a channel is Connected.</p>
      <WhatsAppConnectCard canEdit={!!cv} />
      <WhatsAppInbox />

      {qh?.pending > 0 && (
        <p className="mt-2 rounded-lg border border-orange-200 bg-orange-50 p-2 text-xs text-orange-800" data-testid="comm-queue-health">
          {qh.pending} pending: {qh.eligible} eligible for live sending, {qh.quarantined} quarantined, {Object.values(qh.would_quarantine || {}).reduce((a, b) => a + b, 0)} would be held for review (queued while live sending was off, or older than 2 hours). Held messages are never sent automatically.
        </p>
      )}

      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {[["Accepted", (c.accepted || 0) + (c.delivered || 0) + (c.read || 0), "text-emerald-600"], ["Logged", c.logged, "text-slate-500"], ["Pending", c.pending, "text-amber-600"], ["Unknown", c.unknown, "text-orange-600"], ["Failed", c.failed, "text-rose-600"], ["Quarantined", c.quarantined, "text-orange-700"], ["Legacy sent", c.sent, "text-slate-400"]].map(([l, v, cls]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`comm-metric-${l.toLowerCase()}`}>
            <p className="text-xs uppercase tracking-wide text-slate-500">{l}</p>
            <p className={`mt-1 text-2xl font-bold tabular ${cls}`}>{v ?? 0}</p>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {[["channel", ["", "email", "whatsapp", "sms"]], ["status", ["", "accepted", "delivered", "read", "logged", "pending", "unknown", "failed", "skipped", "sent"]]].map(([key, opts]) => (
          <select key={key} data-testid={`comm-filter-${key}`} value={filters[key]} onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))} className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm">
            {opts.map((o) => <option key={o} value={o}>{o ? o[0].toUpperCase() + o.slice(1) : `All ${key}s`}</option>)}
          </select>
        ))}
      </div>

      <div className="mt-4">
        {isLoading ? <Loading /> : (data?.items?.length ? (
          <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
            <table className="w-full text-sm" data-testid="communications-table">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-4 py-3">When</th><th className="px-4 py-3">Event</th><th className="px-4 py-3">Channel</th><th className="px-4 py-3">To</th><th className="px-4 py-3">Status</th></tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {data.items.map((m) => (
                  <tr key={m.id} className="hover:bg-slate-50" data-testid={`comm-row-${m.id}`}>
                    <td className="px-4 py-3 whitespace-nowrap text-slate-500">{fdt(m.created_at)}</td>
                    <td className="px-4 py-3 font-medium text-slate-800">{(m.event || "").replace(/_/g, " ")}</td>
                    <td className="px-4 py-3 capitalize">{m.channel} <span className="text-xs text-slate-400">({m.mode})</span></td>
                    <td className="px-4 py-3 text-slate-500">{m.recipient || "—"}</td>
                    <td className="px-4 py-3"><StatusPill status={m.status} />{m.quarantine && <span data-testid={`comm-quarantine-${m.id}`} title={m.quarantine.reason} className="ml-1 rounded-full border border-orange-300 px-2 py-0.5 text-[10px] text-orange-700">Held for review</span>}
                      {m.event === "invoice_sent" && m.status === "sending" && <button onClick={() => recover.mutate(m.id)} disabled={recover.isPending} data-testid={`comm-recover-${m.id}`} className="ml-1 rounded-full border border-slate-300 px-2 py-0.5 text-[10px] text-slate-700 hover:bg-slate-50">Mark interrupted</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : <Empty icon={MessageSquare} title="No messages yet" subtitle="Bookings, payments and reminders will appear here." />)}
      </div>
    </div>
  );
}
